import { useEffect, useState } from "react";
import { Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSite } from "@/context/SiteContext";
import { fileUrl } from "@/lib/api";
import { BRAND, divisionName } from "@/lib/content";

const W = 1080;
const H = 1350;
const NAVY = "#00274C";
const GOLD = "#FFCB05";
const PITCH = "#0A0D14";

const loadImg = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const cover = (ctx, img, x, y, w, h) => {
  const s = Math.max(w / img.width, h / img.height);
  const iw = img.width * s;
  const ih = img.height * s;
  ctx.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2.6, iw, ih);
};

const fitFont = (ctx, text, max, start, family) => {
  let size = start;
  do {
    ctx.font = `italic 900 ${size}px ${family}`;
    size -= 4;
  } while (ctx.measureText(text).width > max && size > 40);
  return size + 4;
};

async function drawCard(a, logoSrc) {
  const display = '"Barlow Condensed", Impact, sans-serif';
  const mono = '"JetBrains Mono", monospace';
  await Promise.all([document.fonts.load(`italic 900 100px "Barlow Condensed"`), document.fonts.load(`700 30px "JetBrains Mono"`)]).catch(() => {});
  const [photo, logo] = await Promise.all([loadImg(fileUrl(a.photo_url)), loadImg(logoSrc)]);
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");

  ctx.fillStyle = NAVY;
  ctx.fillRect(0, 0, W, H);
  if (photo) cover(ctx, photo, 0, 0, W, H);
  else {
    ctx.strokeStyle = "rgba(255,203,5,0.08)";
    ctx.lineWidth = 3;
    for (let i = -H; i < W; i += 28) {
      ctx.beginPath();
      ctx.moveTo(i, H);
      ctx.lineTo(i + H, 0);
      ctx.stroke();
    }
  }
  const g = ctx.createLinearGradient(0, H * 0.35, 0, H);
  g.addColorStop(0, "rgba(10,13,20,0)");
  g.addColorStop(0.55, "rgba(0,39,76,0.85)");
  g.addColorStop(1, PITCH);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  const top = ctx.createLinearGradient(0, 0, 0, 320);
  top.addColorStop(0, "rgba(10,13,20,0.75)");
  top.addColorStop(1, "rgba(10,13,20,0)");
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, W, 320);

  if (a.jersey) {
    ctx.font = `italic 900 420px ${display}`;
    ctx.textAlign = "right";
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(255,203,5,0.6)";
    ctx.strokeText(String(a.jersey), W - 30, 400);
    ctx.textAlign = "left";
  }
  if (logo) {
    const lw = 230;
    ctx.drawImage(logo, 50, 40, lw, (logo.height / logo.width) * lw);
  }

  ctx.fillStyle = GOLD;
  ctx.font = `700 26px ${mono}`;
  ctx.fillText("OFFICIAL ALLSTAR • THE COAST'S NEXT UP", 60, H - 470);

  const badges = [divisionName(a.division), a.position].filter(Boolean);
  let bx = 60;
  ctx.font = `italic 900 46px ${display}`;
  badges.forEach((b, i) => {
    const t = b.toUpperCase();
    const bw = ctx.measureText(t).width + 40;
    ctx.fillStyle = i === 0 ? GOLD : "#FFFFFF";
    ctx.fillRect(bx, H - 440, bw, 64);
    ctx.fillStyle = NAVY;
    ctx.fillText(t, bx + 20, H - 391);
    bx += bw + 14;
  });

  const name = (a.name || "").toUpperCase();
  const size = fitFont(ctx, name, W - 120, 170, display);
  ctx.font = `italic 900 ${size}px ${display}`;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(name, 56, H - 230);

  ctx.font = `700 30px ${mono}`;
  ctx.fillStyle = "#CBD5E1";
  const meta = [a.jersey && `#${a.jersey}`, a.home_org].filter(Boolean).join("  •  ").toUpperCase();
  if (meta) ctx.fillText(meta, 60, H - 170);

  ctx.fillStyle = GOLD;
  ctx.beginPath();
  ctx.moveTo(0, H - 110);
  ctx.lineTo(W, H - 130);
  ctx.lineTo(W, H);
  ctx.lineTo(0, H);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = NAVY;
  ctx.font = `italic 900 52px ${display}`;
  ctx.fillText("TRI COUNTY ALLSTARS", 60, H - 42);
  ctx.font = `700 20px ${mono}`;
  ctx.textAlign = "right";
  ctx.fillText("DIFFERENT PARKS. ONE COAST. ONE MISSION.", W - 60, H - 48);
  ctx.textAlign = "left";

  return c;
}

const slug = (s) => (s || "athlete").toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const ShareCardDialog = ({ athlete, onClose }) => {
  const { settings } = useSite();
  const [url, setUrl] = useState("");
  const [blob, setBlob] = useState(null);

  useEffect(() => {
    let alive = true;
    setUrl("");
    drawCard(athlete, settings.logo_url ? fileUrl(settings.logo_url) : BRAND.logo).then((c) => {
      c.toBlob((b) => {
        if (!alive || !b) return;
        setBlob(b);
        setUrl(URL.createObjectURL(b));
      }, "image/png");
    });
    return () => {
      alive = false;
    };
  }, [athlete, settings.logo_url]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  const fname = `tri-county-allstars-${slug(athlete.name)}.png`;
  const file = blob ? new File([blob], fname, { type: "image/png" }) : null;
  const canShare = !!file && typeof navigator !== "undefined" && navigator.canShare?.({ files: [file] });

  const share = async () => {
    try {
      await navigator.share({ files: [file], title: `${athlete.name} — Tri County Allstars`, text: "Different Parks. One Coast. One Mission. #TriCountyAllstars" });
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing isn't available here — download the image instead.");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-ink border-line max-h-[95vh] overflow-y-auto" data-testid="share-card-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black uppercase italic">Share Card</DialogTitle>
          <DialogDescription className="text-slate-400">Download or share {athlete.name}'s official Tri County Allstars graphic.</DialogDescription>
        </DialogHeader>
        <div className="aspect-[4/5] w-full bg-pitch flex items-center justify-center">
          {url ? <img src={url} alt={`${athlete.name} share card`} className="w-full h-full object-contain" data-testid="share-card-preview" /> : <Loader2 className="h-8 w-8 animate-spin text-gold" data-testid="share-card-loading" />}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <a href={url || undefined} download={fname} className={`btn-gold !text-base ${url ? "" : "pointer-events-none opacity-50"}`} data-testid="share-card-download"><Download className="h-4 w-4" /> Download</a>
          {canShare ? (
            <button onClick={share} className="btn-line !text-base" data-testid="share-card-native-share"><Share2 className="h-4 w-4" /> Share</button>
          ) : (
            <p className="text-xs text-slate-400 self-center">Post the downloaded image to Instagram, Facebook or TikTok.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
